import { isAccessAllowed, isNavItemVisible, type AccessInfo } from '@/shared/rbac'
import type { Tour, TourAccessContext, TourAccessRule, TourStep } from '../types'

/** `isAccessAllowed` (the RequireAccess decision) + the optional sidebar-group rule. */
export function ruleAllows(rule: TourAccessRule | undefined, ctx: TourAccessContext): boolean {
  if (!rule) return true
  if (!isAccessAllowed(ctx.access, rule)) return false
  if (rule.navGroup) {
    const group = ctx.navGroups.find((g) => g.label === rule.navGroup)
    if (!group?.items.some((item) => isNavItemVisible(item, ctx.access, ctx.has3d))) return false
  }
  if (rule.userFeature && Boolean(ctx.userFeatures?.[rule.userFeature.key]) !== rule.userFeature.on) return false
  return true
}

export function resolveStepRoute(step: TourStep, access: AccessInfo): string | undefined {
  return typeof step.route === 'function' ? step.route(access) : step.route
}

export function findPageTour(pathname: string, tours: readonly Tour[]): Tour | undefined {
  return tours.find((t) => t.kind === 'page' && t.match?.test(pathname))
}

/**
 * May the engine SEND the user to `path`? Only if a page tour covers it and
 * that tour's rule (a copy of the route guard) passes. Unknown paths are
 * refused — fail closed, never navigate into a 403.
 */
export function canNavigateTo(path: string, ctx: TourAccessContext, tours: readonly Tour[]): boolean {
  const tour = findPageTour(path.split(/[?#]/)[0], tours)
  return !!tour && ruleAllows(tour.access, ctx)
}

export function isStepEligible(
  tour: Tour,
  step: TourStep,
  ctx: TourAccessContext,
  tours: readonly Tour[],
): boolean {
  if (!ruleAllows(tour.access, ctx) || !ruleAllows(step.access, ctx)) return false
  const route = resolveStepRoute(step, ctx.access)
  return route === undefined || canNavigateTo(route, ctx, tours)
}

export function filterSteps(tour: Tour, ctx: TourAccessContext, tours: readonly Tour[]): TourStep[] {
  return tour.steps.filter((step) => isStepEligible(tour, step, ctx, tours))
}
